'use client';

import * as React from 'react';
import classNames from 'classnames';
import { Modifier, usePopper } from 'react-popper';
import Link from 'next/link';
import LoadingContent from '@/components/ui/LoadingContent';
import { Portal } from '@/components/ui/Portal';
import Icon, { IconsAvailable } from '../Icon';
import styles from './styles.module.scss';
import { useKeypress } from '@/components/hooks/useKeyPress';
import { Placement } from '@popperjs/core';
import ImageWithFallback from '@/components/ui/ImageWithFallback';

export type ButtonSize = 'default' | 'small' | 'medium' | 'large';

export interface IButtonProps {
  /** title to use */
  title?: React.ReactNode;
  /** control to use */
  control?: React.ReactNode;
  /** size to use */
  size?: ButtonSize;
  /** click event, should not be used with type 'anchor' */
  onClick?: (e: React.SyntheticEvent) => void;
  /** intent variation */
  theme?: 'default' | 'primary' | 'telegram' | 'discrete' | 'link';
  /** disable clicking */
  disabled?: boolean;
  /** disable clicking and display loader */
  loading?: boolean;
  /** DOM target element name attribute */
  name?: string;
  /** DOM target element id attribute */
  id?: string;
  /** Icon to display */
  iconLeft?: IconsAvailable;
  /** Icon to display */
  iconRight?: IconsAvailable;
  /** Image to display */
  imageLeft?: string;
  /** Image to display */
  imageRight?: string;
  /** className to append */
  className?: string;
  /** Type param to pass */
  type?: 'button' | 'submit' | 'reset';
  /** Use link instead of button */
  href?: string;
  /** link target */
  target?: string;
  /** Menu */
  menu?: React.ReactNode;
  /** Menu placement */
  menuPlacement?: Placement;
  /** Effects */
  effects?: {
    lines: boolean;
  };
}

const sameWidth: Modifier<unknown> = {
  name: 'sameWidth',
  enabled: true,
  phase: 'beforeWrite',
  requires: ['computeStyles'],
  fn: ({ state }) => {
    state.styles.popper.minWidth = `${state.rects.reference.width}px`;
  },
  effect: ({ state }) => {
    const w = state.elements.reference.getBoundingClientRect();
    state.elements.popper.style.minWidth = `${w.width}px`;
  },
};

/**
 * Displays button
 */
const Button: React.FunctionComponent<IButtonProps> = (props) => {
  const [referenceElement, setReferenceElement] = React.useState<HTMLSpanElement | null>(null);
  const [popperElement, setPopperElement] = React.useState<HTMLSpanElement | null>(null);
  const [menuVisible, setMenuVisible] = React.useState(false);
  const [focusWithin, setFocusWithin] = React.useState(false);
  const [focusableElements, setFocusableElements] = React.useState<HTMLElement[]>([]);

  const ariaId = 'aria-' + React.useId();

  const { styles: popperStyles, attributes } = usePopper(referenceElement, popperElement, {
    strategy: 'fixed',
    placement: props.menuPlacement ? props.menuPlacement : 'bottom-end',
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          padding: 20,
        },
      },
      {
        name: 'offset',
        options: {
          offset: [!!props.title ? 0 : 16, 4],
        },
      },
      sameWidth,
    ],
  });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.code === 'Space') {
      e.preventDefault();
      e.stopPropagation();
      setMenuVisible(!menuVisible);
    }
    if (!e.shiftKey && e.code === 'Tab' && menuVisible) {
      const firstFocusableElement = focusableElements[0];
      if (firstFocusableElement) {
        e.preventDefault();
        firstFocusableElement.focus();
      }
    }
  };

  const returnFocus = () => {
    if (menuVisible && referenceElement) {
      referenceElement.focus();
    }
  };

  const handleFocus = () => {
    setFocusWithin(true);
  };

  const handleBlur = () => {
    setFocusWithin(false);

    setTimeout(() => {
      const target = document.activeElement;

      // There is no focus within any of elements
      if (
        !!target &&
        !(referenceElement && referenceElement.contains(target as Node)) &&
        !(popperElement && popperElement.contains(target as Node))
      ) {
        setMenuVisible(false);
      }
    }, 1);
  };

  const handleFocusTrapStart: React.FocusEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    if (referenceElement) {
      referenceElement.focus();
    }
  };
  const handleFocusTrapEnd: React.FocusEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    if (referenceElement) {
      setMenuVisible(false);
      referenceElement.focus();
    }
  };

  useKeypress(
    'Escape',
    'keyup',
    () => {
      if (menuVisible) {
        returnFocus();
        setMenuVisible(false);
      }
    },
    [menuVisible],
  );

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;
      if (menuVisible) {
        if (target && popperElement && popperElement.contains(target as Node)) {
          setFocusWithin(true);
          setMenuVisible(false);
          return;
        }
        if (target && referenceElement && !referenceElement.contains(target as Node)) {
          setFocusWithin(true);
          setMenuVisible(false);
        }
      }
    };

    globalThis.addEventListener('click', handleClickOutside);
    return () => {
      globalThis.removeEventListener('click', handleClickOutside);
    };
  }, [menuVisible, referenceElement, popperElement, focusWithin]);

  React.useEffect(() => {
    const str = [
      'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"])',
      'details:not([tabindex="-1"])',
      'input:not([disabled]):not([tabindex="-1"])',
      'select:not([disabled]):not([tabindex="-1"])',
      'textarea:not([disabled]):not([tabindex="-1"])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    const focusableElements: HTMLElement[] = popperElement ? Array.from(popperElement.querySelectorAll(str)) : [];

    setFocusableElements(focusableElements);
  }, [menuVisible, props.menu, popperElement]);

  const buttonClass = classNames(
    'button',
    styles['button'],
    {
      [styles['disabled']]: props.disabled,
      [styles['loading']]: props.loading,
      [styles['has-menu']]: !!props.menu,

      [styles['size-default']]: !props.size || props.size === 'default',
      [styles['size-small']]: props.size === 'small',
      [styles['size-large']]: props.size === 'large',
      [styles['size-medium']]: props.size === 'medium',

      [styles['theme-default']]: props.theme === 'default' || props.theme === 'telegram' || !props.theme,
      [styles['theme-telegram']]: props.theme === 'telegram',
      [styles['theme-primary']]: props.theme === 'primary',
      [styles['theme-discrete']]: props.theme === 'discrete',
      [styles['theme-link']]: props.theme === 'link',

      [styles['effect-lines']]: props.effects?.lines,
    },
    props.className,
  );

  const renderContents = (
    <span className={styles['button__inner']}>
      {props.iconLeft && (
        <Icon
          wrapperClassName={classNames(styles['button__icon'], styles['button__icon-left'])}
          width={20}
          height={20}
          kind={props.iconLeft}
        />
      )}
      {props.imageLeft && (
        <div className={classNames(styles['button__icon'], styles['button__icon-left'])}>
          <ImageWithFallback
            width={20}
            height={20}
            src={props.imageLeft}
            alt=""
            priority={false}
            icon="money-circle"
            loading="eager"
          />
        </div>
      )}
      {(!!props.title || props.title === '') && <span className={styles['button__text']}>{props.title}</span>}
      {props.control && <span className={styles['button__control']}>{props.control}</span>}
      {!props.menu && props.iconRight && (
        <Icon
          wrapperClassName={classNames(styles['button__icon'], styles['button__icon-right'])}
          width={20}
          height={20}
          kind={props.iconRight}
        />
      )}
      {props.imageRight && (
        <div className={classNames(styles['button__icon'], styles['button__icon-right'])}>
          <ImageWithFallback
            width={20}
            height={20}
            src={props.imageRight}
            alt=""
            priority={false}
            icon="money-circle"
            loading="eager"
          />
        </div>
      )}
      {props.menu && (
        <Icon
          wrapperClassName={classNames(styles['button__arrow'], styles['button__icon-right'])}
          width={16}
          height={16}
          kind={menuVisible ? 'chevron-close' : 'chevron-open'}
        />
      )}
      {props.loading && (
        <LoadingContent
          size="tiny"
          layout="absolute"
        />
      )}
    </span>
  );

  return (
    <span
      className={buttonClass}
      draggable={false}
    >
      {props.href && (
        <Link
          ref={setReferenceElement}
          href={props.href}
          className={styles['button__element']}
          id={props.id || ariaId}
          draggable={false}
          prefetch={false}
          aria-busy={props.loading}
          target={props.target}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onClick={(e) => {
            if (props.menu) {
              setMenuVisible(!menuVisible);
            }
            if (props.onClick) {
              props.onClick(e);
            }
            if (!props.onClick && props.href && props.href.startsWith('#')) {
              const target = props.href.substring(1);
              const elem = document.getElementById(target);
              if (elem) {
                e.preventDefault();
                elem.scrollIntoView({
                  block: 'start',
                  behavior: 'smooth',
                });
              }
            }
          }}
          aria-haspopup={props.menu ? 'true' : 'false'}
          aria-expanded={menuVisible ? 'true' : 'false'}
          aria-controls={`${props.id || ariaId}-menu`}
        >
          {renderContents}
        </Link>
      )}
      {props.control && (
        <div
          ref={setReferenceElement}
          className={styles['button__element']}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
        >
          {renderContents}
        </div>
      )}
      {!props.href && !props.control && (
        <button
          ref={setReferenceElement}
          className={styles['button__element']}
          type={props.type || 'button'}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onClick={(e) => {
            if (props.menu) {
              setMenuVisible(!menuVisible);
            }
            if (props.onClick) {
              props.onClick(e);
            }
          }}
          disabled={props.disabled}
          name={props.name}
          id={props.id || ariaId}
          aria-busy={props.loading}
          aria-haspopup={props.menu ? 'true' : 'false'}
          aria-expanded={menuVisible ? 'true' : 'false'}
          aria-controls={`${props.id || ariaId}-menu`}
        >
          {renderContents}
        </button>
      )}
      {props.menu && menuVisible && (
        <Portal>
          <div
            tabIndex={0}
            className={styles['button__menu__focustrap']}
            onFocus={handleFocusTrapStart}
          />
          <div
            className={styles['button__menu']}
            ref={setPopperElement}
            style={popperStyles.popper}
            role="menu"
            aria-labelledby={props.id || ariaId}
            id={`${props.id || ariaId}-menu`}
            tabIndex={-1}
            onBlur={handleBlur}
            {...attributes.popper}
          >
            {props.menu}
          </div>
          <div
            tabIndex={0}
            className={styles['button__menu__focustrap']}
            onFocus={handleFocusTrapEnd}
          />
        </Portal>
      )}
    </span>
  );
};

Button.displayName = 'Button';

export default Button;
